import { DateTime } from "luxon";
import { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Loader } from "rsuite";
import { Button } from "../../components/common/Button";
import { ShareBtn } from "../../components/common/ShareBtn";
import { useActivitySearch } from "../../components/MainSearchForm/activity";
import { handleNonValidImgFn, isValidURL, TranslationContext } from "../../umbilical_tools";
import type { GenericCard as GenericCardType } from "./types";

export function GenericCard({
  card,
  onViewHotel,
}: {
  card: GenericCardType;
  onViewHotel: (params: { hotelId?: string | null; destinationId?: string | null }) => void;
}) {
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  let payload: any = null;
  try {
    payload = card.action_payload ? JSON.parse(card.action_payload) : null;
  } catch {
    // Ignore parse errors
  }

  // Make sure that if we have past dates in the payload then we set those to
  // the future.
  if (payload) {
    if (payload?.date_from) {
      const dt = DateTime.fromISO(payload.date_from);
      if (dt.startOf("day") <= DateTime.now().startOf("day")) {
        payload.date_from = DateTime.now().plus({ days: 7 });
        payload.date_to = DateTime.now().plus({ days: 8 });
      }
    }
  }

  const { onFormUpdate: onActivityFormUpdate, onFormSearch: onActivityFormSearch } =
    useActivitySearch();

  async function onActivitySearch() {
    if (!payload) return;
    setLoading(true);
    await onActivityFormUpdate({
      form: {
        date_from: payload?.date_from,
        date_to: payload?.date_to,
        destination_code: [payload.destination_code],
        destination_meta: payload.destination_meta,
      },
    });
    await onActivityFormSearch({
      source_entity: payload?.source_entity,
      market_type: payload?.market_type,
      history,
      setLoadingSearchBtn: setLoading,
    });
    setLoading(false);
  }

  function onHotelSearch(payloadData: any) {
    if (payloadData?.query?.type === "destination") {
      onViewHotel({ destinationId: payloadData?.query?.id?.[0] });
    } else {
      onViewHotel({ hotelId: payloadData?.query?.id?.[0] });
    }
  }

  const shareUrl = isValidURL(card.path) ? card.path : `${window.location.origin}/${card.path}`;

  return (
    <div className="w-[80vw] lg:w-64 xl:w-72 justify-self-center rounded-lg shadow-[2px_6px_11px_0px_rgba(0,0,0,0.15)] relative">
      {loading && <Loader center backdrop />}
      <div>
        <img
          src={card.image_url}
          className="w-full h-auto max-h-40 object-cover rounded-t-lg"
          alt={card.title ?? ""}
          onError={handleNonValidImgFn}
        />
        {card.path && <ShareBtn placeBtmRight={false} hasFullPath={true} path={shareUrl} />}
      </div>
      <div className="grid grid-rows-[max-content_max-content_1fr_max-content] p-6 lg:h-72">
        <h4 className="whitespace-nowrap text-ellipsis overflow-hidden h-[34px]">{card.title}</h4>
        <hr className="w-full" />
        <p className="overflow-hidden line-clamp-5">{card.description}</p>
        <div className="pt-3 w-max">
          {(card.path || payload) && (
            <Button
              text={card.call_to_action_label || t("more")}
              size="small"
              onClick={() => {
                if (payload && (payload?.query?.id ?? []).length > 0) {
                  onHotelSearch(payload);
                } else if (payload) {
                  onActivitySearch();
                } else if (isValidURL(card.path)) {
                  window.open(card.path, "_blank");
                } else if (card.path) {
                  history.push(card.path);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
